import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  TablePagination,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  LocalOffer as TagIcon,
  Telegram as TelegramIcon,
  TravelExplore as TravelExploreIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { articleApi } from "../../services/api";
import type { Article } from "../../models";
import ArticleTags from "../../components/cms/ArticleTags";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SeriesAutocomplete from '../../components/Cricket/SeriesAutocomplete';
import MatchAutocomplete from '../../components/Cricket/MatchAutocomplete';
import useDebounce from "../../hooks/useDebounce";

interface PaginationState {
  page: number;
  pageSize: number;
  totalCount: number;
}

interface ArticleListParams {
  page: number;
  page_size: number;
  search?: string;
  ordering?: string;
  article_type?: string;
  is_draft?: boolean;
  published?: boolean;
}

const LoadingTableRow = () => (
  <>
    {[...Array(5)].map((_, index) => (
      <TableRow key={index}>
        <TableCell>
          <Skeleton animation="wave" />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={80} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={120} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={80} />
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Skeleton
              animation="wave"
              width={24}
              height={24}
              variant="circular"
            />
            <Skeleton
              animation="wave"
              width={24}
              height={24}
              variant="circular"
            />
            <Skeleton
              animation="wave"
              width={24}
              height={24}
              variant="circular"
            />
          </Stack>
        </TableCell>
      </TableRow>
    ))}
  </>
);

const VITE_APP_URL = import.meta.env.VITE_APP_URL || '';

const ArticleList: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedArticleForTag, setSelectedArticleForTag] =
    useState<Article | null>(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<Article["article_type"] | "all">(
    "all"
  );
  const [statusFilter, setStatusFilter] = useState<
    "all" | "published" | "draft"
  >("all");

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 20,
    totalCount: 0,
  });

  // Add state for tagged match/series filters
  const [taggedSeries, setTaggedSeries] = useState<any>(null);
  const [taggedMatch, setTaggedMatch] = useState<any>(null);

  // Schedule & Telegram Broadcast Dialog states
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleArticle, setScheduleArticle] = useState<Article | null>(null);
  const [schedulePublishAt, setSchedulePublishAt] = useState("");
  const [scheduleAutoPublish, setScheduleAutoPublish] = useState(false);
  const [telegramScheduleEnabled, setTelegramScheduleEnabled] = useState(false);
  const [telegramScheduledAt, setTelegramScheduledAt] = useState("");
  const [telegramMessage, setTelegramMessage] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    fetchArticles();
  }, [
    pagination.page,
    pagination.pageSize,
    debouncedSearchQuery,
    typeFilter,
    statusFilter,
    taggedSeries,
    taggedMatch,
  ]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params: ArticleListParams & any = {
        page: pagination.page + 1, // API uses 1-based pagination
        page_size: pagination.pageSize,
        search: debouncedSearchQuery,
        ordering: "-created_at",
      };

      // Add filters if not 'all'
      if (typeFilter !== "all") {
        params.article_type = typeFilter;
      }
      if (statusFilter !== "all") {
        params.is_draft = statusFilter === "draft";
        params.published = statusFilter === "published";
      }
      // Add tagged series/match filter
      if (taggedSeries) {
        params.tagged_content_type = 'series';
        params.tagged_object_id = taggedSeries.id;
      } else if (taggedMatch) {
        params.tagged_content_type = 'match';
        params.tagged_object_id = taggedMatch.id;
      }

      const response = await articleApi.list(params);
      setArticles(response.data.results);
      setPagination((prev) => ({
        ...prev,
        totalCount: response.data.count,
      }));
      setError(null);
    } catch (error) {
      console.error("Error fetching articles:", error);
      setError("Failed to fetch articles");
      setNotification({
        open: true,
        message: "Failed to fetch articles",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({
      ...prev,
      page: 0,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  const updateArticleInList = (updatedArticle: Article) => {
    setArticles((prevArticles) =>
      prevArticles.map((article) =>
        article.id === updatedArticle.id ? updatedArticle : article
      )
    );
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setTypeFilter("all");
    setStatusFilter("all");
    setTaggedSeries(null);
    setTaggedMatch(null);
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }));
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this article?")) {
      try {
        await articleApi.delete(id);
        fetchArticles();
        setNotification({
          open: true,
          message: "Article deleted successfully",
          severity: "success",
        });
      } catch (err) {
        console.error("Error deleting article:", err);
        setNotification({
          open: true,
          message: "Failed to delete article",
          severity: "error",
        });
      }
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    article: Article
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedArticle(article);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedArticle(null);
  };

  const formatDatetimeForInput = (isoString?: string | null) => {
    if (!isoString) return "";
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return "";
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return "";
    }
  };

  const handleOpenScheduleDialog = (article: Article) => {
    setScheduleArticle(article);
    setSchedulePublishAt(formatDatetimeForInput(article.scheduled_publish_at));
    setScheduleAutoPublish(Boolean(article.auto_publish_enabled));

    const tg = article.social_sharing?.telegram;
    setTelegramScheduleEnabled(Boolean(tg?.scheduled_at || tg?.status === 'pending'));
    setTelegramScheduledAt(formatDatetimeForInput(tg?.scheduled_at));
    setTelegramMessage(tg?.message || "");
    setScheduleDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveSchedule = async () => {
    if (!scheduleArticle?.id) return;
    setSavingSchedule(true);
    try {
      // 1. Save article publishing schedule
      await articleApi.schedulePublish(scheduleArticle.id, {
        scheduled_publish_at: schedulePublishAt ? new Date(schedulePublishAt).toISOString() : null,
        auto_publish_enabled: scheduleAutoPublish,
      });

      // 2. Save Telegram broadcast schedule and curated message
      const updatedSocial = { ...(scheduleArticle.social_sharing || {}) };
      if (telegramScheduleEnabled || telegramMessage.trim()) {
        updatedSocial.telegram = {
          enabled: true,
          ...(updatedSocial.telegram || {}),
          message: telegramMessage,
          scheduled_at: telegramScheduledAt ? new Date(telegramScheduledAt).toISOString() : null,
          status: updatedSocial.telegram?.status === 'published' ? 'published' : 'pending',
        };
      } else if (updatedSocial.telegram) {
        delete updatedSocial.telegram.scheduled_at;
      }

      await articleApi.setupSocialSharing(scheduleArticle.id, {
        social_sharing: updatedSocial,
      });

      setNotification({
        open: true,
        message: "Publishing and Telegram broadcast settings saved successfully!",
        severity: "success",
      });
      setScheduleDialogOpen(false);
      fetchArticles();
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.response?.data?.error || "Failed to save schedule settings",
        severity: "error",
      });
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleRequestIndexing = async (article: Article) => {
    handleMenuClose();
    if (!article.id) return;
    try {
      const res = await articleApi.requestIndexing(article.id);
      setNotification({
        open: true,
        message: res.data?.message || "Google Indexing requested successfully!",
        severity: "success",
      });
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.response?.data?.error || "Failed to request Google Indexing",
        severity: "error",
      });
    }
  };

  const handleRequestIndexNow = async (article: Article) => {
    handleMenuClose();
    if (!article.id) return;
    try {
      const res = await articleApi.requestIndexNow(article.id);
      setNotification({
        open: true,
        message: res.data?.message || "IndexNow (Bing/Yandex) requested successfully!",
        severity: "success",
      });
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.response?.data?.error || "Failed to submit to IndexNow",
        severity: "error",
      });
    }
  };

  const handleShare = async (article: Article) => {
    navigate(`/cms/articles/${article.id}/share`);
    handleMenuClose();
  };

  const handleSendTelegram = async (article: Article) => {
    handleMenuClose();
    if (!article.id) return;

    if (
      !window.confirm(
        `Are you sure you want to send "${article.title}" to the Telegram channel?`
      )
    ) {
      return;
    }

    try {
      const res = await articleApi.sendToTelegram(article.id);
      setNotification({
        open: true,
        message:
          res.data?.message ||
          "Article sent to Telegram channel successfully!",
        severity: "success",
      });
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error ||
        "Failed to send article to Telegram channel";
      setNotification({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    }
  };

  const handleSendTestTelegram = async (article: Article) => {
    handleMenuClose();
    if (!article.id) return;

    try {
      const res = await articleApi.sendToTestTelegram(article.id);
      setNotification({
        open: true,
        message:
          res.data?.message ||
          "Test message sent to Telegram channel (@test_ai11news) successfully!",
        severity: "success",
      });
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error ||
        "Failed to send test message to Telegram channel";
      setNotification({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    }
  };

  const handleTagManagement = (article: Article) => {
    setSelectedArticleForTag(article);
    setTagDialogOpen(true);
    handleMenuClose();
  };

  const getArticleTypeColor = (
    type: Article["article_type"]
  ): "primary" | "secondary" | "success" | "warning" | "info" | "default" => {
    const colors: Record<
      Article["article_type"],
      "primary" | "secondary" | "success" | "warning" | "info" | "default"
    > = {
      story: "primary",
      post: "secondary",
      news: "success",
      shorts: "warning",
      gallery: "info",
    };
    return colors[type] || "default";
  };

  const getTagLabel = (tag: any) => {
    if (!tag.content_object) return "Unknown";

    switch (tag.content_type) {
      case "match":
      case "series":
      case "player":
        return `${tag.content_object.name} (${tag.tag_type})`;
      default:
        return "Unknown";
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Articles</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate("/cms/articles/new")}
        >
          New Article
        </Button>
      </Stack>

      {/* Filters Section */}
      <Paper sx={{ p: 1, mb: 2, backgroundColor: (theme) => theme.palette.background.paper }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
                endAdornment: searchQuery && (
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) =>
                  setTypeFilter(
                    e.target.value as Article["article_type"] | "all"
                  )
                }
              >
                <SelectMenuItem value="all">All Types</SelectMenuItem>
                <SelectMenuItem value="post">Blog Post</SelectMenuItem>
                <SelectMenuItem value="story">Story</SelectMenuItem>
                <SelectMenuItem value="news">News</SelectMenuItem>
                <SelectMenuItem value="shorts">Shorts</SelectMenuItem>
                <SelectMenuItem value="gallery">Gallery</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as "all" | "published" | "draft"
                  )
                }
              >
                <SelectMenuItem value="all">All Status</SelectMenuItem>
                <SelectMenuItem value="published">Published</SelectMenuItem>
                <SelectMenuItem value="draft">Draft</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SeriesAutocomplete
              value={taggedSeries}
              onChange={(value) => {
                setTaggedSeries(value);
                setTaggedMatch(null);
              }}
              disabled={!!taggedMatch}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <MatchAutocomplete
              value={taggedMatch}
              onChange={(value) => {
                setTaggedMatch(value);
                setTaggedSeries(null);
              }}
              disabled={!!taggedSeries}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              size="small"
              sx={{ minWidth: 0, px: 1 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Published At</TableCell>
              <TableCell>Last Modified</TableCell>
              <TableCell>Draft</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <LoadingTableRow />
            ) : articles.length > 0 ? (
              articles.map((article) => (
                <TableRow key={article.id}>
                  <TableCell>{article.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={article.article_type}
                      color={getArticleTypeColor(article.article_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={article.published ? "Published" : "Draft"}
                        color={article.published ? "success" : "default"}
                        size="small"
                      />
                      {article.latest_draft && (
                        <Chip label="Has Draft" color="warning" size="small" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {article.taggeditem_set?.map((tag, index) => (
                        <Chip
                          key={index}
                          label={getTagLabel(tag)}
                          size="small"
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {article.author_name || <Typography variant="body2" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    {article.created_by_name || <Typography variant="body2" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    {article.published_at
                      ? new Date(article.published_at).toLocaleDateString()
                      : ""}
                  </TableCell>
                  <TableCell>
                    {new Date(article.updated_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {article.is_draft && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(`/cms/articles/${article.id}/edit`)
                        }
                      >
                        Edit Draft
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      {/* Preview Button */}
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          component="a"
                          href={
                            article.published
                              ? `${VITE_APP_URL}/blogs/${article.slug}`
                              : `${VITE_APP_URL}/blogs/${article.uuid}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                      {/* View Button */}
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/cms/articles/${article.id}/view`)
                          }
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Edit Button */}
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/cms/articles/${article.id}/edit`)
                          }
                          disabled={article.is_draft}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Send to Telegram Button */}
                      <Tooltip title="Send to Telegram Channel">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSendTelegram(article)}
                        >
                          <TelegramIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* More Button */}
                      <Tooltip title="More">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, article)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" py={2}>
                    No articles found matching your filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>

      {/* Article Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {selectedArticle && (
          <>
            <MenuItem onClick={() => handleOpenScheduleDialog(selectedArticle)}>
              <ListItemIcon>
                <ScheduleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schedule & Social</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleShare(selectedArticle)}>
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Share</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => handleSendTelegram(selectedArticle)}>
              <ListItemIcon>
                <TelegramIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Send to Telegram</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => handleSendTestTelegram(selectedArticle)}>
              <ListItemIcon>
                <TelegramIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText>Send Test to Telegram (@test_ai11news)</ListItemText>
            </MenuItem>

            {selectedArticle.published && (
              <MenuItem onClick={() => handleRequestIndexing(selectedArticle)}>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" color="info" />
                </ListItemIcon>
                <ListItemText>Request Google Indexing</ListItemText>
              </MenuItem>
            )}

            {selectedArticle.published && (
              <MenuItem onClick={() => handleRequestIndexNow(selectedArticle)}>
                <ListItemIcon>
                  <CloudUploadIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>Submit to IndexNow (Bing)</ListItemText>
              </MenuItem>
            )}

            <MenuItem onClick={() => handleTagManagement(selectedArticle)}>
              <ListItemIcon>
                <TagIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Manage Tags</ListItemText>
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleDelete(selectedArticle.id!);
                handleMenuClose();
              }}
              sx={{ color: "error.main" }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Tag Management Dialog */}
      <Dialog
        open={tagDialogOpen}
        onClose={() => setTagDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Tags - {selectedArticleForTag?.title}</DialogTitle>
        <DialogContent>
          {selectedArticleForTag && (
            <ArticleTags
              value={selectedArticleForTag.taggeditem_set || []}
              onChange={async (tags) => {
                try {
                  // Handle tag changes
                  const currentTags = selectedArticleForTag.taggeditem_set || [];

                  // Find tags to add (tags that don't exist in currentTags)
                  const tagsToAdd = tags.filter(
                    (newTag) =>
                      !currentTags.some(
                        (currentTag) =>
                          currentTag.content_type === newTag.content_type &&
                          currentTag.object_id === newTag.object_id &&
                          currentTag.tag_type === newTag.tag_type
                      )
                  );

                  // Find tags to remove (currentTags that don't exist in tags)
                  const tagsToRemove = currentTags.filter(
                    (currentTag) =>
                      !tags.some(
                        (newTag) =>
                          newTag.content_type === currentTag.content_type &&
                          newTag.object_id === currentTag.object_id &&
                          newTag.tag_type === currentTag.tag_type
                      )
                  );

                  // Add new tags
                  for (const tag of tagsToAdd) {
                    await articleApi.addTag(selectedArticleForTag.id!, {
                      tag_type: tag.tag_type,
                      content_type: tag.content_type,
                      object_id: tag.object_id,
                    });
                  }

                  // Remove tags
                  for (const tag of tagsToRemove) {
                    await articleApi.removeTag(selectedArticleForTag.id!, {
                      tag_id: tag.id,
                    });
                  }

                  // Fetch the updated article to get fresh data
                  const updatedArticleResponse = await articleApi.getById(selectedArticleForTag.id!);
                  const updatedArticle = updatedArticleResponse.data;

                  // Update the article in the list
                  updateArticleInList(updatedArticle);

                  // Update the selected article for tag management
                  setSelectedArticleForTag(updatedArticle);

                  setNotification({
                    open: true,
                    message: "Tags updated successfully",
                    severity: "success",
                  });
                } catch (error) {
                  console.error("Error updating tags:", error);
                  setNotification({
                    open: true,
                    message: "Failed to update tags",
                    severity: "error",
                  });
                }
              }}
              disabled={false}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Schedule & Telegram Broadcast Dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Schedule Publishing & Telegram Broadcast
          {scheduleArticle?.title ? ` - ${scheduleArticle.title}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Section 1: Article Publishing Schedule */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                1. Article Publishing Schedule
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Configure when this article should go live on the website.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <TextField
                  label="Scheduled Publish Date & Time"
                  type="datetime-local"
                  value={schedulePublishAt}
                  onChange={(e) => setSchedulePublishAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 260 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={scheduleAutoPublish}
                      onChange={(e) => setScheduleAutoPublish(e.target.checked)}
                    />
                  }
                  label="Auto-publish when scheduled time arrives"
                />
              </Stack>
              {scheduleArticle?.published && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This article is already published. Changing publish schedule will not unpublish it.
                </Alert>
              )}
            </Box>

            <Divider />

            {/* Section 2: Telegram Channel Broadcast Schedule */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <TelegramIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  2. Telegram Channel Broadcast (Decoupled)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Schedule the Telegram notification at or after publication. The message will only be sent once the article is published.
              </Typography>

              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={telegramScheduleEnabled}
                        onChange={(e) => setTelegramScheduleEnabled(e.target.checked)}
                      />
                    }
                    label="Enable Scheduled Telegram Broadcast"
                  />
                  <TextField
                    label="Telegram Broadcast Date & Time"
                    type="datetime-local"
                    value={telegramScheduledAt}
                    onChange={(e) => setTelegramScheduledAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={!telegramScheduleEnabled}
                    sx={{ minWidth: 260 }}
                    helperText="Leave blank or set to publish time to broadcast as soon as published"
                  />
                </Stack>

                {scheduleArticle?.social_sharing?.telegram?.status && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Current Telegram Status:</Typography>
                    <Chip
                      size="small"
                      label={scheduleArticle.social_sharing.telegram.status.toUpperCase()}
                      color={
                        scheduleArticle.social_sharing.telegram.status === "published"
                          ? "success"
                          : scheduleArticle.social_sharing.telegram.status === "failed"
                          ? "error"
                          : "warning"
                      }
                    />
                    {scheduleArticle.social_sharing.telegram.published_at && (
                      <Typography variant="caption" color="text.secondary">
                        (Sent on {new Date(scheduleArticle.social_sharing.telegram.published_at).toLocaleString()})
                      </Typography>
                    )}
                  </Box>
                )}

                <TextField
                  label="Curated Telegram Message / Teaser"
                  multiline
                  rows={4}
                  fullWidth
                  value={telegramMessage}
                  onChange={(e) => setTelegramMessage(e.target.value)}
                  placeholder="Engaging emoji-rich teaser crafted specifically for Telegram channel. Leave blank to fallback to standard bilingual summary."
                  helperText="If provided, this curated teaser will be sent instead of the default bilingual website summary."
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)} disabled={savingSchedule}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSchedule}
            disabled={savingSchedule}
          >
            {savingSchedule ? "Saving..." : "Save Schedule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArticleList;
